/**
 * アプリ設定（BGM / SE のミュート状態、言語など）のデフォルト値と制約。
 *
 * 設計方針:
 *   - UI が ON/OFF トグル（2 値）であることに合わせ、内部状態も boolean で持つ。
 *     「コードが仕様書」を重視した選択。数値音量に変更する場合は AppSettings と
 *     loadSettings のマイグレーションを合わせて更新すること。
 *   - 実際の再生音量は固定値（`BGM_PLAYBACK_VOLUME` / SE 側は
 *     `src/config/se.ts` の `SE_DEFINITIONS[*].masterVolume`）を使う。ミュートは
 *     再生経路側で「0 にする or 再生スキップ」のガードで実現する。
 *
 * 参考: `.claude/rules/no-hardcode.md`
 */

import { SUPPORTED_LOCALES, type SupportedLocale } from '../i18n/locales'

/**
 * BGM をミュートしていないときに HTMLAudioElement.volume へ渡す値（0〜1）。
 *
 * 旧実装で `useAudio.ts` がハードコードしていた 0.4 をここに移した。UX を
 * 変えないため値は据え置き。将来スライダー UI を導入する際はこの定数を廃止し、
 * `AppSettings` に `bgmVolume: number` を追加する想定。
 */
export const BGM_PLAYBACK_VOLUME = 0.4

/**
 * BGM の初期ミュート状態（ネイティブアプリ基準）。
 *
 * ネイティブアプリ（Capacitor Android / iOS）はユーザーが「ゲームアプリ」として
 * 起動する前提なので、従来どおり音 ON で始める。
 *
 * ブラウザ（LINE ミニアプリ / Web）は音量調整の摩擦で離脱する事故が報告されたため、
 * `services/settings.ts` の read 側ゲートが保存値に関わらず無音に倒す。
 * この定数はそのゲートより手前の既定値なので、ここは両環境共通で false のままにする。
 */
export const DEFAULT_BGM_MUTED = false

/** SE の初期ミュート状態（BGM と同じ扱い）。 */
export const DEFAULT_SE_MUTED = false

/** サポートするロケール一覧。日本語を含む18言語。 */
export { SUPPORTED_LOCALES }
export type AppLocale = SupportedLocale

/** ロケール初期値。 */
export const DEFAULT_LOCALE: AppLocale = 'ja'

/** localStorage キー（タイトル名前空間を含めて衝突を避ける）。 */
export const SETTINGS_STORAGE_KEY = 'luckyanimal_nawabari_settings'

/**
 * 「音 OFF 既定化」の一回限り移行フラグを保存するキー。
 *
 * `SETTINGS_STORAGE_KEY` 側の値で移行判定をすると、旧 numeric スキーマ
 * （`bgmVolume` / `seVolume`）からの移行ロジック（`services/settings.ts`）と
 * 判定が絡んで壊れやすいため、専用キーに切り出している。
 */
export const SOUND_OPTIN_STORAGE_KEY = 'luckyanimal_nawabari_sound_optin'

/**
 * 適用済みの「音 OFF 既定化」の版。
 * この値と保存値が一致しない起動では、保存済みの設定に関わらず一度 OFF に戻す。
 * 将来もう一度全ユーザーを OFF に戻したくなったら、この値を上げる。
 */
export const SOUND_OPTIN_VERSION = 'v1'

/**
 * 設定データ構造（localStorage に保存する JSON 形式）。
 *
 * スキーマを変更する場合は `loadSettings()` のバリデーション
 * および旧形式マイグレーションも更新すること。
 */
export interface AppSettings {
  bgmMuted: boolean
  seMuted: boolean
  locale: AppLocale
}

/** デフォルト設定の完成形。パース失敗 / 未保存時に利用。 */
export const DEFAULT_SETTINGS: AppSettings = {
  bgmMuted: DEFAULT_BGM_MUTED,
  seMuted: DEFAULT_SE_MUTED,
  locale: DEFAULT_LOCALE,
}
